import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, from } from 'rxjs';
import { firstDayOfMonth, lastDayOfMonth } from '@progress/kendo-date-math';
import { CarRepairBillingHeader } from '../car-repair-billing/car-repair-billing-header.model';
import { RailroadTransactionCarData } from '../car-repair-billing/railroad-transaction-car-data.model';
import { SendArToIntacctInvoice } from '../../components/common/send-ar-to-intacct/send-ar-to-intacct-invoice.model';
import { map, switchMap, concatMap } from 'rxjs/operators';
import { ArInvoiceType } from 'app/shared/arInvoiceType.model';
import { CreditArDetail } from 'app/components/common/credit-ar-details/credit-ar-detail.model';
import { ScrapServiceEventInvoiceDetail } from '../program-work/scrap-service-event-invoice-detail.model';

@ Injectable({providedIn: 'root'})
export class InvoiceService {
	private url: string;
	constructor(private httpClient: HttpClient) {
		this.url = 'api/invoice';
	}

	public saveCredit(creditData: CarRepairBillingHeader[]): Observable< any> {
		return this.httpClient.post(this .url + '/createCredit', creditData);
	}

	public saveCreditArDetails(arDetailCredits: CreditArDetail[], arInvId: number, arInvType: ArInvoiceType): Observable<any> {
		return this.httpClient.post<any>(this.url + '/createArCredits', arDetailCredits,
		{
			params: {
				'arId': arInvId.toString(),
				'invType': arInvType.toString(),
			},
		});
	}

	public createCba(cbaData: RailroadTransactionCarData[]): Observable< any> {
		return this.httpClient.post(this .url + '/createCba', cbaData);
	}

	public createDdcRecord(data: RailroadTransactionCarData, type: string): Observable<any> {
		return this.httpClient.post(`${this.url}/createDdcRecord?ddcType=${type}`, data);
	}

	public get(id: number): Observable<any> {
		return this.httpClient.get<any>(this .url + '/' + id);
	}

	public getHeaders(id: number): Observable< CarRepairBillingHeader[]> {
		return this.httpClient.get<CarRepairBillingHeader[]>(this .url + '/getHeaders/' + id);
	}

	public getStipFmvInvoice(id: number): Observable<any> {
		return this.httpClient.get<any>(this .url + '/getStipFmvInvoice/' + id);
	}

	public getStipFmvInvoiceBySeId(serviceEventId: number): Observable<CarRepairBillingHeader> {
		return this.httpClient.get<CarRepairBillingHeader>(this .url + `/getStipFmvInvoicebySeId?serviceEventId=${serviceEventId}`);
	}

	public saveStipFmvInvoice(invoiceType: ArInvoiceType, serviceEventId: number, purchaseOrderNumber: string, total: number, invoiceDate: Date, dueDate: Date): Observable<any> {
		return this.httpClient.post(`${this.url}/saveStipFmvInvoice?invoiceType=${invoiceType}&serviceEventId=${serviceEventId}&purchaseOrderNumber=${purchaseOrderNumber}&total=${total}&invoiceDate=${invoiceDate.toLocaleDateString()}&dueDate=${dueDate.toLocaleDateString()}`, null);
	}

	public saveScrapInvoice(serviceEventId: number, pricePerGrossTon: number, actualScrapWeight: number, umlerTareWeight: number, scrapDeliveryFee: number): Observable<any> {
		return this.httpClient.post(`${this.url}/saveScrapInvoice?serviceEventId=${serviceEventId}&pricePerGrossTon=${pricePerGrossTon}&actualScrapWeight=${actualScrapWeight}&umlerTareWeight=${umlerTareWeight}&scrapDeliveryFee=${scrapDeliveryFee}`, null);
	}

	public saveScrapInvoices(scrapServiceEventInvoiceDetails: ScrapServiceEventInvoiceDetail[]): Observable<any> {
		return this.httpClient.post(`${this.url}/saveScrapInvoices`, scrapServiceEventInvoiceDetails);
	}

	public updateInvoiceDateByNumber(glPostingDate: Date, invoiceNumber: string): Observable<any> {
		return this.httpClient.post(`${this.url}/updateInvoiceDateByNumber?glPostingDate=${glPostingDate.toLocaleDateString()}&invoiceNumber=${invoiceNumber}`, null);
	}

	public createArPreview(arInvoiceIds: number[], billingPeriod: Date, invoiceType: ArInvoiceType): Observable<any[]> {
		return this.httpClient.post<any[]>(this.url + '/createArPreview', arInvoiceIds,
		{
			params: {				
				'startDate': new Date (firstDayOfMonth(billingPeriod).setMonth(billingPeriod.getMonth() - 6)).toDateString(),
				'endDate': lastDayOfMonth(billingPeriod).toDateString()
			},
		});
	}
	
	public sendArsSequentially(subject: Subject<SendArToIntacctInvoice[]>, invoiceType: ArInvoiceType) {
		return subject.pipe(
			switchMap(val => from(val).pipe(concatMap(x => this.sendArToIntacct(x.invoiceId, invoiceType, x.glPostingDate, x.accountingDueDate))
			))
		);
	}

	public sendArToIntacct(invoiceId: number, invoiceType: ArInvoiceType, glPostingDate: Date, dueDate: Date = undefined): Observable<string> {
		return this.httpClient.post<string>(this.url + '/sendArToAccounting', invoiceId,
			{
				params: {
					'invType': invoiceType.toString(),
					'glPostingDate': glPostingDate.toLocaleDateString(),
					'dueDate': dueDate? dueDate.toLocaleDateString() : undefined
				},
				responseType: 'text' as 'json'
			});
	}

	public getInvoiceContacts(arInvoiceIds: number[], invoiceType: ArInvoiceType, contactType: string): Observable<any>{
		return this.httpClient.post<Observable<any>>(this.url + '/getInvoiceContacts', arInvoiceIds,
			{
				params: {
					'invoiceType': invoiceType.toString(),
					'contactType': contactType
				}
			});
	}
}
